import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Package,
  CheckCircle,
  Truck,
  Plane,
  Ship,
  FileText,
  Calendar,
  RefreshCw,
  ArrowRight,
  Plus,
  Camera,
  MapPin,
  Clock,
} from 'lucide-react';
import heroBg from '../assets/auth/logistics-hero.png';
import API_BASE from '../services/apiBase';
import { useAuth } from '../features/auth/AuthContext';
import usePackageCameraScan from '../hooks/usePackageCameraScan';
import { packageStatusLabel, TRACKING_STATUS_OPTIONS } from '../utils/packageTrack';
import { validateScanForm } from '../utils/packageValidation';
import '../styles/tracking-page.css';
import '../styles/package-tracking.css';

const RECENT_KEY = 'jet_recent_tracking';

const SCAN_STATUSES = TRACKING_STATUS_OPTIONS;

function nowDateInput() {
  return new Date().toISOString().slice(0, 10);
}

function nowTimeInput() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatShipmentStatus(status) {
  const map = {
    pending: 'Pending',
    processing: 'Processing',
    'picked-up': 'Picked Up',
    'in-transit': 'In Transit',
    delivered: 'Delivered',
    exception: 'Exception',
  };
  return map[String(status || '').toLowerCase()] || status;
}

function mapShipmentToTrackingResult(shipment) {
  const origin = [shipment.origin?.city, shipment.origin?.country].filter(Boolean).join(', ') || '—';
  const destination = [shipment.destination?.city, shipment.destination?.country].filter(Boolean).join(', ') || '—';
  const dims = shipment.cargo?.dimensions;
  const dimStr = dims?.length && dims?.width && dims?.height
    ? `${dims.length} x ${dims.width} x ${dims.height} ${dims.unit || 'cm'}`
    : '—';
  const weight = shipment.cargo?.weight
    ? `${shipment.cargo.weight} ${shipment.cargo.weightUnit || 'kg'}`
    : '—';

  const timeline = (shipment.timeline || []).map((ev) => {
    const d = ev.date ? new Date(ev.date) : null;
    return {
      date: d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : '',
      time: ev.time || '',
      status: ev.status,
      location: ev.location || '',
      description: ev.description || '',
    };
  });

  return {
    trackingNumber: shipment.trackingNumber,
    status: formatShipmentStatus(shipment.status),
    origin,
    destination,
    estimatedDelivery: shipment.estimatedDelivery
      ? new Date(shipment.estimatedDelivery).toISOString().slice(0, 10)
      : undefined,
    actualDelivery: shipment.actualDelivery
      ? new Date(shipment.actualDelivery).toISOString().slice(0, 10)
      : undefined,
    currentLocation: shipment.currentLocation || origin,
    service: shipment.service?.name || shipment.service?.type || 'Cargo',
    weight,
    dimensions: dimStr,
    timeline,
  };
}

const formatDisplayDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatEventDateTime = (dateStr, timeStr) => {
  if (!dateStr) return '—';
  const raw = String(dateStr);
  if (raw.includes('T')) {
    const iso = new Date(raw);
    if (!Number.isNaN(iso.getTime())) {
      return iso.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }
  }
  const d = new Date(`${raw}T${timeStr || '12:00'}:00`);
  if (Number.isNaN(d.getTime())) return `${raw} ${timeStr || ''}`.trim();
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const guessLocationTag = (place) => {
  if (!place) return '—';
  const part = place.split(',')[0].trim().replace(/[^A-Za-z]/g, '');
  return (part.slice(0, 3).toUpperCase() || '—').slice(0, 3);
};

const getTimelineVisual = (status) => {
  const s = status.toLowerCase();
  if (s.includes('delivered')) return { rowTone: 'done', dot: 'green', Icon: CheckCircle };
  if (s.includes('out for')) return { rowTone: '', dot: 'blue', Icon: Plus };
  if (s.includes('arrived')) return { rowTone: '', dot: 'blue', Icon: Truck };
  if (s.includes('transit')) return { rowTone: '', dot: 'blue', Icon: Plane };
  if (s.includes('departed')) return { rowTone: '', dot: 'blue', Icon: Package };
  if (s.includes('picked')) return { rowTone: '', dot: 'blue', Icon: Package };
  if (s.includes('processing')) return { rowTone: '', dot: 'blue', Icon: Package };
  return { rowTone: '', dot: 'blue', Icon: Package };
};

const getServiceIcon = (service) => {
  switch (String(service).toLowerCase()) {
    case 'air cargo':
      return <Plane className="w-5 h-5" />;
    case 'sea cargo':
      return <Ship className="w-5 h-5" />;
    default:
      return <Truck className="w-5 h-5" />;
  }
};

const Tracking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'staff';

  const [trackingNumber, setTrackingNumber] = useState('');
  const [recentKeys, setRecentKeys] = useState([]);
  const [trackingResult, setTrackingResult] = useState(null);
  const [packageResult, setPackageResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [scanStatus, setScanStatus] = useState('malaysia-warehouse');
  const [scanEventDate, setScanEventDate] = useState(nowDateInput);
  const [scanEventTime, setScanEventTime] = useState(nowTimeInput);
  const [scanBusy, setScanBusy] = useState(false);
  const [scanMsg, setScanMsg] = useState('');

  useEffect(() => {
    const p = searchParams.get('p');
    const n = searchParams.get('n');
    if (p) setTrackingNumber(String(p).toUpperCase());
    else if (n) setTrackingNumber(String(n).toUpperCase());
  }, [searchParams]);

  const fetchPackage = useCallback(async (code) => {
    const c = String(code || '').trim().toUpperCase();
    if (!c) return null;
    try {
      const res = await fetch(`${API_BASE}/api/packages/track/${encodeURIComponent(c)}`);
      const json = await res.json();
      if (!res.ok) return null;
      return json.data;
    } catch {
      return null;
    }
  }, []);

  const fetchShipment = useCallback(async (trackingNo) => {
    const c = String(trackingNo || '').trim().toUpperCase();
    if (!c) return null;
    try {
      const res = await fetch(`${API_BASE}/api/shipments/track/${encodeURIComponent(c)}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) return null;
      return mapShipmentToTrackingResult(json.data);
    } catch {
      return null;
    }
  }, []);

  const recordRecent = useCallback((num) => {
    const key = String(num).toUpperCase().trim();
    if (!key) return;
    try {
      const prev = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((x) => typeof x === 'string');
      const next = [key, ...prev.filter((x) => x !== key)].slice(0, 3);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      setRecentKeys(next);
    } catch {
      setRecentKeys([key]);
    }
  }, []);

  const lookupCode = useCallback(
    async (rawCode, { updateUrl = true, clearPrevious = true } = {}) => {
      const id = String(rawCode || '').trim().toUpperCase();
      if (!id) {
        setError('Please enter a tracking number.');
        return;
      }
      setLoading(true);
      setError('');
      if (clearPrevious) {
        setTrackingResult(null);
        setPackageResult(null);
      }

      const pkg = await fetchPackage(id);
      if (pkg) {
        recordRecent(id);
        setPackageResult(pkg);
        setTrackingResult(null);
        if (updateUrl) navigate(`/tracking?p=${encodeURIComponent(id)}`, { replace: true });
        setLoading(false);
        return;
      }

      const shipment = await fetchShipment(id);
      setPackageResult(null);
      if (shipment) {
        recordRecent(id);
        setTrackingResult(shipment);
        if (updateUrl) navigate(`/tracking?n=${encodeURIComponent(id)}`, { replace: true });
      } else {
        setError('Tracking number not found. Please check your tracking number and try again.');
      }
      setLoading(false);
    },
    [fetchPackage, fetchShipment, navigate, recordRecent]
  );

  const handleCameraDetect = useCallback((parsed) => {
    setTrackingNumber(parsed);
    setCameraOn(false);
    lookupCode(parsed);
  }, [lookupCode]);

  const { videoRef, cameraError } = usePackageCameraScan({
    onDetect: handleCameraDetect,
    active: cameraOn,
  });

  const pParam = searchParams.get('p');

  useEffect(() => {
    if (pParam) {
      lookupCode(pParam, { updateUrl: false });
    }
  }, [pParam, lookupCode]);

  useEffect(() => {
    if (cameraError) setCameraOn(false);
  }, [cameraError]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      setRecentKeys(raw ? JSON.parse(raw) : []);
    } catch {
      setRecentKeys([]);
    }
  }, []);

  const clearRecent = () => {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {
      /* ignore */
    }
    setRecentKeys([]);
  };

  const chipsToShow = useMemo(() => recentKeys.slice(0, 3), [recentKeys]);

  const runLookup = useCallback(
    (clearPrevious = true) => {
      lookupCode(trackingNumber, { clearPrevious });
    },
    [lookupCode, trackingNumber]
  );

  const handleTracking = (e) => {
    e.preventDefault();
    runLookup();
  };

  const handleRefreshDetails = () => {
    if (packageResult?.packageCode) {
      lookupCode(packageResult.packageCode, { updateUrl: false, clearPrevious: false });
      return;
    }
    if (!trackingResult || !trackingNumber.trim()) return;
    runLookup(false);
  };

  const handleStaffScan = async () => {
    if (!packageResult?.packageCode || !user?.token) return;
    const validation = validateScanForm({
      code: packageResult.packageCode,
      status: scanStatus,
      eventDate: scanEventDate,
      eventTime: scanEventTime,
    });
    if (!validation.ok) {
      setScanMsg(validation.message);
      return;
    }
    setScanBusy(true);
    setScanMsg('');
    try {
      const res = await fetch(
        `${API_BASE}/api/packages/${encodeURIComponent(validation.code)}/scan`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({
            status: scanStatus,
            eventDate: scanEventDate,
            eventTime: scanEventTime,
            description: `Status updated to ${packageStatusLabel(scanStatus)}`,
          }),
        }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Scan failed');
      setPackageResult(json.data);
      setScanMsg('Tracking updated successfully.');
    } catch (e) {
      setScanMsg(e.message || 'Update failed');
    } finally {
      setScanBusy(false);
    }
  };

  const hblDisplay = packageResult?.parentHblNo || packageResult?.collectionNo || '—';

  const sortedTimeline = useMemo(() => {
    if (!trackingResult?.timeline) return [];
    return [...trackingResult.timeline].sort((a, b) => {
      const ta = new Date(`${a.date}T${a.time || '12:00'}:00`).getTime();
      const tb = new Date(`${b.date}T${b.time || '12:00'}:00`).getTime();
      return tb - ta;
    });
  }, [trackingResult]);

  const lastUpdatedLabel = useMemo(() => {
    if (!sortedTimeline.length) return null;
    const latest = sortedTimeline[0];
    return formatEventDateTime(latest.date, latest.time);
  }, [sortedTimeline]);

  const statusPillClass = (status) => {
    const s = String(status).toLowerCase();
    if (s.includes('delivered')) return 'track-status-pill track-status-pill--success';
    if (s.includes('transit')) return 'track-status-pill track-status-pill--transit';
    return 'track-status-pill track-status-pill--muted';
  };

  const etaDisplay = trackingResult?.actualDelivery || trackingResult?.estimatedDelivery;

  return (
    <div className="track-page">
      <section className="track-hero" aria-labelledby="track-hero-title">
        <div
          className="track-hero__bg"
          style={{ backgroundImage: `url(${heroBg})` }}
        />
        <div className="track-hero__overlay" />
        <div className="track-hero__inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <span className="track-hero__eyebrow">Track shipment</span>
            <h1 id="track-hero-title" className="track-hero__title">
              Track Your Shipment
            </h1>
            <p className="track-hero__lead">
              Stay updated with your shipment status in real-time. Enter your tracking number or scan the QR code on your box sticker.
            </p>

            <form className="track-pill-form" onSubmit={handleTracking}>
              <div className="track-pill">
                <Package className="w-5 h-5" aria-hidden />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter tracking or package code (e.g. '01818-01')"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value.toUpperCase())}
                  required
                  aria-label="Tracking number"
                />
                <button
                  type="button"
                  className={`track-pill__camera${cameraOn ? ' track-pill__camera--on' : ''}`}
                  onClick={() => setCameraOn((v) => !v)}
                  aria-label={cameraOn ? 'Turn camera off' : 'Scan QR code with camera'}
                  title={cameraOn ? 'Camera off' : 'Scan QR / barcode'}
                >
                  <Camera className="w-5 h-5" aria-hidden />
                </button>
                <button type="submit" className="track-pill__submit" disabled={loading}>
                  {loading ? (
                    <span className="track-pill__spinner" aria-hidden />
                  ) : (
                    <>
                      Track Now
                      <ArrowRight className="w-4 h-4" aria-hidden />
                    </>
                  )}
                </button>
              </div>
            </form>

            {cameraOn ? (
              <div className="track-hero__camera">
                <video ref={videoRef} className="track-hero__camera-video" muted playsInline />
                <p className="track-hero__camera-hint">Hold the sticker QR or barcode within the camera frame</p>
              </div>
            ) : null}
            {cameraError ? <div className="track-hero__error">{cameraError}</div> : null}

            {error ? <div className="track-hero__error">{error}</div> : null}

            <div className="track-recent">
              <span className="track-recent__label">
                Recently tracked:
                <button
                  type="button"
                  className="track-recent__clear"
                  onClick={clearRecent}
                  title="Clear recent"
                  aria-label="Clear recent tracking numbers"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </span>
              <div className="track-recent__chips">
                {chipsToShow.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className="track-recent__chip"
                    onClick={() => setTrackingNumber(code)}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="track-body">
        {packageResult ? (
          <motion.div
            className="track-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <div className="track-card__grid">
              <aside className="track-sidebar">
                <h2 className="track-sidebar__head">
                  <Package className="w-5 h-5" aria-hidden />
                  Package overview
                </h2>
                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">HBL</div>
                  <div className="track-sidebar__value">{hblDisplay}</div>
                </div>
                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">SUB NO</div>
                  <div className="track-sidebar__value">{packageResult.subNo}</div>
                </div>
                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">Package code</div>
                  <div className="track-sidebar__value">{packageResult.packageCode}</div>
                </div>
                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">Status</div>
                  <span className="track-status-pill track-status-pill--transit">
                    {packageStatusLabel(packageResult.currentStatus)}
                  </span>
                </div>
                {packageResult.consigneeSnapshot?.name ? (
                  <div className="track-sidebar__meta">
                    <MapPin className="w-4 h-4" style={{ display: 'inline', marginRight: 4 }} />
                    <strong>{packageResult.consigneeSnapshot.name}</strong>
                    <br />
                    {packageResult.consigneeSnapshot.address}
                    <br />
                    {packageResult.consigneeSnapshot.phone}
                  </div>
                ) : null}
              </aside>
              <div className="track-main">
                <div className="track-main__head">
                  <h3 className="track-main__title">
                    <Clock className="w-5 h-5" style={{ display: 'inline', marginRight: 6 }} />
                    Package timeline
                  </h3>
                  <div className="track-main__updated">
                    <button type="button" onClick={handleRefreshDetails} disabled={loading} title="Refresh">
                      <RefreshCw className={`w-4 h-4${loading ? ' track-icon-spin' : ''}`} />
                    </button>
                  </div>
                </div>
                <div className="track-timeline">
                  {(packageResult.timeline || []).slice().reverse().map((ev, index) => (
                    <motion.div
                      key={`${ev.date}-${ev.time}-${ev.status}-${index}`}
                      className="track-tl-item"
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.06, duration: 0.35 }}
                    >
                      <div className="track-tl-item__dot track-tl-item__dot--blue">
                        <Package className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                      </div>
                      <div className="track-tl-item__body">
                        <div className="track-tl-item__top">
                          <h4 className="track-tl-item__status">{packageStatusLabel(ev.status)}</h4>
                          <span className="track-tl-item__time">
                            {formatEventDateTime(ev.date, ev.time)}
                          </span>
                        </div>
                        <p className="track-tl-item__desc">{ev.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
            {isStaff && user?.token ? (
              <div className="pkg-track-staff" style={{ marginTop: '1.25rem' }}>
                <h3>Staff — Update tracking</h3>
                <label>
                  Status
                  <select value={scanStatus} onChange={(e) => setScanStatus(e.target.value)}>
                    {SCAN_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
                <div className="pkg-scan-datetime">
                  <label>
                    Date
                    <input
                      type="date"
                      value={scanEventDate}
                      onChange={(e) => setScanEventDate(e.target.value)}
                      required
                    />
                  </label>
                  <label>
                    Time
                    <input
                      type="time"
                      value={scanEventTime}
                      onChange={(e) => setScanEventTime(e.target.value)}
                      required
                    />
                  </label>
                </div>
                <button type="button" onClick={handleStaffScan} disabled={scanBusy}>
                  {scanBusy ? 'Updating…' : 'Update status'}
                </button>
                {scanMsg ? <p className="pkg-track-scan-msg">{scanMsg}</p> : null}
              </div>
            ) : null}
          </motion.div>
        ) : null}
        {trackingResult ? (
          <motion.div
            className="track-card"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <div className="track-card__grid">
              <aside className="track-sidebar">
                <h2 className="track-sidebar__head">
                  <FileText className="w-5 h-5" aria-hidden />
                  Shipment overview
                </h2>

                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">Tracking number</div>
                  <div className="track-sidebar__value">{trackingResult.trackingNumber}</div>
                </div>

                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">Service type</div>
                  <div className="track-sidebar__row--inline">
                    {getServiceIcon(trackingResult.service)}
                    <span className="track-sidebar__value" style={{ fontSize: '1rem' }}>
                      {trackingResult.service}
                    </span>
                  </div>
                </div>

                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">Status</div>
                  <span className={statusPillClass(trackingResult.status)}>{trackingResult.status}</span>
                </div>

                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">Origin</div>
                  <div className="track-sidebar__row--inline">
                    <span className="track-sidebar__value" style={{ fontSize: '0.95rem' }}>
                      {trackingResult.origin}
                    </span>
                    <span className="track-code">{guessLocationTag(trackingResult.origin)}</span>
                  </div>
                </div>

                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">Destination</div>
                  <div className="track-sidebar__row--inline">
                    <span className="track-sidebar__value" style={{ fontSize: '0.95rem' }}>
                      {trackingResult.destination}
                    </span>
                    <span className="track-code">{guessLocationTag(trackingResult.destination)}</span>
                  </div>
                </div>

                <div className="track-sidebar__row">
                  <div className="track-sidebar__label">
                    {trackingResult.actualDelivery ? 'Delivered on' : 'Estimated delivery'}
                  </div>
                  <div className="track-sidebar__eta">
                    <Calendar className="w-5 h-5" aria-hidden />
                    <span className="track-sidebar__eta-date">{formatDisplayDate(etaDisplay)}</span>
                  </div>
                </div>

                <div className="track-sidebar__meta">
                  <strong>Current location:</strong> {trackingResult.currentLocation}
                  <br />
                  <span>
                    {trackingResult.weight} · {trackingResult.dimensions}
                  </span>
                </div>
              </aside>

              <div className="track-main">
                <div className="track-main__head">
                  <h3 className="track-main__title">Tracking details</h3>
                  {lastUpdatedLabel ? (
                    <div className="track-main__updated">
                      Last updated: {lastUpdatedLabel}
                      <button type="button" onClick={handleRefreshDetails} disabled={loading} title="Refresh">
                        <RefreshCw className={`w-4 h-4${loading ? ' track-icon-spin' : ''}`} />
                      </button>
                    </div>
                  ) : null}
                </div>

                <div className="track-timeline">
                  {sortedTimeline.map((event, index) => {
                    const vis = getTimelineVisual(event.status);
                    const Icon = vis.Icon;
                    const rowClass = ['track-tl-item', vis.rowTone === 'done' ? 'track-tl-item--done' : '']
                      .filter(Boolean)
                      .join(' ');
                    const dotClass = `track-tl-item__dot track-tl-item__dot--${vis.dot}`;

                    return (
                      <motion.div
                        key={`${event.date}-${event.time}-${event.status}-${index}`}
                        className={rowClass}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.06, duration: 0.35 }}
                      >
                        <div className={dotClass}>
                          <Icon className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                        </div>
                        <div className="track-tl-item__body">
                          <div className="track-tl-item__top">
                            <h4 className="track-tl-item__status">{event.status}</h4>
                            <span className="track-tl-item__time">{formatEventDateTime(event.date, event.time)}</span>
                          </div>
                          <p className="track-tl-item__desc">{event.description}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}
      </div>

      {!trackingResult && !packageResult && !loading ? (
        <p className="track-empty-hint">Demo: try tracking numbers JET123456 or JET789012.</p>
      ) : null}
    </div>
  );
};

export default Tracking;
